import { memo, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogButton, DialogDescription, DialogTitle } from '~/components/ui/LegacyDialog';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import type { RequestStatus } from '~/lib/stores/chat';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';

const WAIT_TIME_MS = 60;

interface RateLimitDialogProps {
  open: boolean;
  requestStatus: RequestStatus;
  onUpgrade: () => void;
  onRetry: () => void;
  onClose: () => void;
}

export const RateLimitDialog = memo<RateLimitDialogProps>(({ open, requestStatus, onUpgrade, onRetry, onClose }) => {
  const [countdown, setCountdown] = useState(WAIT_TIME_MS);
  const [isRetrying, setIsRetrying] = useState(true);

  const resetCountdown = useCallback(() => {
    setCountdown(WAIT_TIME_MS);
    setIsRetrying(false);
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (open && countdown > 0 && !isRetrying) {
      timer = setTimeout(() => {
        setCountdown((prevCount) => prevCount - 1);
      }, 1000);
    }

    return () => clearTimeout(timer);
  }, [open, countdown, isRetrying]);

  useEffect(() => {
    if (countdown <= 0) {
      setIsRetrying(true);

      onRetry();

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.BoltConcurrentFreeUsersRetry,
        },
      });
    }
  }, [countdown, open, resetCountdown]);

  useEffect(() => {
    // call failed again
    if (isRetrying && requestStatus === 'ERROR') {
      resetCountdown();
    }
  }, [isRetrying, requestStatus]);

  useEffect(() => {
    if (open) {
      resetCountdown();
    }
  }, [open, resetCountdown]);

  return (
    <Dialog open={open} maxWidth={600} showCloseButton={true} onClose={onClose}>
      <DialogTitle>Heavy usage</DialogTitle>
      <DialogDescription>
        {!isRetrying ? (
          <p className="mb-4">
            We are currently experiencing heavy usage. We will retry your request in{' '}
            <span className="font-bold">{countdown}</span> seconds.
          </p>
        ) : (
          <div className="flex mb-4">
            <LoadingDots text="Retrying" />
          </div>
        )}
        <p>Or upgrade now to skip the line!</p>
      </DialogDescription>
      <DialogActions>
        <DialogButton variant="primary" onClick={onUpgrade}>
          Upgrade
        </DialogButton>
      </DialogActions>
    </Dialog>
  );
});

RateLimitDialog.displayName = 'RateLimitDialog';
